import orthancFileSystemManager, {
  hasPersistedDirectoryAccess,
} from '../../orthanc_duplicata/core/fileSystemManager';

/**
 * Reads the reports written to the local directory by the caching button.
 *
 * The duplicator names its directories with Orthanc internal ids while the viewer
 * only holds DICOM UIDs, so the inventory it also wrote is the translation table.
 * Read once per page and reduced to the one Map we need, because the raw file is
 * the whole server's /studies, /series and /instances listings — megabytes.
 */

/** `{ bySeries }`: a series DICOM UID → its Orthanc `{ studyId, seriesId }`. */
let indexes = null;

/** The handle the index was built from, so re-granting another folder rebuilds it. */
let indexedHandle = null;

/** Coalesces the concurrent first readers of the index. */
let indexPromise = null;

/**
 * Bumped on every reset, so a build that started before it cannot commit its now
 * outdated result: inventory.json is megabytes, and a duplication finishing while
 * one is being parsed would otherwise leave the stale index pinned for the page.
 */
let generation = 0;

/** Forgets the index. Called when a fresh duplication has written new reports. */
export const resetOfflineReportIndex = () => {
  generation += 1;
  indexes = null;
  indexedHandle = null;
  indexPromise = null;
};

/**
 * The generation the index is currently on.
 *
 * Exposed so a caller that keeps what a read returned can tell that a duplication
 * has written new report.json files since, and re-read instead of serving the copy
 * it holds. Never persisted anywhere by design: the counter must die with the page
 * that owns the caches it qualifies.
 * @returns {number}
 */
export const getOfflineReportGeneration = () => generation;

/**
 * Whether this profile has a local duplication, and therefore whether the disk is
 * the only place a report may come from.
 *
 * Named here, in the report-domain module the readers already go through, rather
 * than reached for in orthanc_duplicata: the flag is a storage fact, this is what it
 * means for a report. Synchronous, so a reader can settle it before its first await.
 *
 * The persisted flag and not the live handle, deliberately: the handle is null at every
 * page start and only comes back once something restores it, so reading it would make
 * the answer depend on when the question is asked. The restore does happen before the
 * panel first renders — ViewerRetrieveStudyData calls loadStudyFromOrthancCache, which
 * restores — but only succeeds while the browser still holds the grant, since
 * requestPermission cannot prompt outside a user gesture.
 *
 * So when the grant is gone the disk is silent, and that silence is the intended
 * answer — an outdated copy from another tier is not an acceptable substitute for a
 * document a radiologist signs off on. Re-granting the directory brings the reports
 * back.
 * @returns {boolean}
 */
export const hasLocalDuplication = () => hasPersistedDirectoryAccess();

/** Reads inventory.json and keeps only the series UID → Orthanc ids mapping. */
const buildIndex = async handle => {
  const file = await orthancFileSystemManager.getFile(handle, 'inventory.json');
  if (!file) return { bySeries: new Map() };

  // The flat `series` array, not `inventory.studies`: the hierarchy nests every
  // instance under its series, which is the expensive shape.
  const { series } = JSON.parse(await file.text());
  const bySeries = new Map();

  (series || []).forEach(entry => {
    const uid = entry.MainDicomTags && entry.MainDicomTags.SeriesInstanceUID;

    // Both ids, because report.json sits in the series directory and that one is
    // nested under its study's: neither id alone can reach it.
    if (uid && entry.ID && entry.ParentStudy) {
      bySeries.set(uid, { studyId: entry.ParentStudy, seriesId: entry.ID });
    }
  });

  return { bySeries };
};

/**
 * The index, built on first use.
 *
 * Conditioned on a live handle and **never** restoring one: restoreDirectoryHandle
 * rehydrates from IndexedDB and then goes through requestPermission, which outside a
 * user gesture resolves to 'prompt' whenever the grant is gone — and clears the handle
 * as a side effect. Its hasLiveAccess() short-circuit only spares a caller whose
 * handle is already live, which is precisely the caller that did not need the restore.
 *
 * Someone else pays that risk once per page, early, before anything holds an access to
 * lose: loadStudyFromOrthancCache on the viewer route, loadCachedSeriesIDs on the
 * study list. This reader rides on whatever they obtained rather than revoking, on a
 * report read, the access the thumbnails and the DICOM loader depend on.
 * @returns {Promise<{bySeries: Map}|null>} null when no directory is live
 */
const getIndex = () => {
  const handle = orthancFileSystemManager.directoryHandle;
  if (!handle) return Promise.resolve(null);

  if (indexes && indexedHandle === handle) {
    return Promise.resolve(indexes);
  }

  if (indexPromise && indexedHandle === handle) return indexPromise;

  const mine = generation;
  indexedHandle = handle;
  indexPromise = buildIndex(handle)
    .then(index => {
      // A reset or another folder happened while we parsed: do not commit.
      if (mine === generation) indexes = index;

      return index;
    })
    .catch(error => {
      console.warn('[offlineReportStore] Could not read the inventory:', error);

      // An unparseable inventory is remembered as "nothing here", otherwise a
      // truncated file would be re-parsed on every single study change.
      if (mine === generation) indexes = { bySeries: new Map() };

      return null;
    });

  return indexPromise;
};

/**
 * Reads studies/{studyId}/series/{seriesId}/report.json.
 * @param {{studyId: string, seriesId: string}} ids The Orthanc ids of the series,
 *   as the index holds them
 * @param {string} expectedSeriesUID The series we are answering for. Required, and
 *   compared rather than trusted: the file names its own series, so a directory
 *   written under a selection rule that has drifted since degrades to a miss instead
 *   of to one series' report shown for another's
 * @returns {Promise<Object|null>}
 */
const readEnvelopeOfSeries = async (ids, expectedSeriesUID) => {
  const seriesDir = await orthancFileSystemManager.getSeriesDirectory(
    ids.studyId,
    ids.seriesId
  );
  if (!seriesDir) return null;

  const file = await orthancFileSystemManager.getFile(seriesDir, 'report.json');
  if (!file) return null;

  const envelope = JSON.parse(await file.text());

  // Kept on its own: a file holding `null` parses without throwing, and the UID
  // comparison below would then read a property of null.
  if (!envelope) return null;

  if (envelope.seriesInstanceUID !== expectedSeriesUID) return null;

  return envelope;
};

/**
 * The envelope written to disk for a series, if the caching button ever ran and
 * the directory is still authorised in this page.
 * @param {string} seriesInstanceUID
 * @returns {Promise<Object|null>} The envelope, null when there is none
 */
export const readOfflineReport = async seriesInstanceUID => {
  try {
    const index = await getIndex();
    if (!index) return null;

    const ids = index.bySeries.get(seriesInstanceUID);
    if (!ids) return null;

    return await readEnvelopeOfSeries(ids, seriesInstanceUID);
  } catch (error) {
    console.warn('[offlineReportStore] Could not read the report:', error);

    return null;
  }
};
